import { partsApiBaseURL, type AppEnvironment } from '@/models/app-environment'

export interface SplashBrand {
  readonly name: string
  readonly logoURL: URL | null
}

/** The splash must not hang on this; a slower answer means no logo. */
const BRAND_TIMEOUT_MS = 3000

const httpURL = (value: unknown): URL | null => {
  if (typeof value !== 'string' || !/^https?:\/\//i.test(value)) return null
  try {
    return new URL(value)
  } catch {
    return null
  }
}

/**
 * The wheel's brand, from the parts API by the launch URL's wheel id
 * (2026-10-13). The assembly job also carries it, but a storefront launch
 * writes that job seconds after this page opens. Never throws: no brand, an
 * error or a timeout is null, and the splash leaves the logo slot blank.
 */
export async function fetchWheelBrand({
  env,
  wheelId,
  fetchImpl = (input, init) => fetch(input, init),
}: {
  env: AppEnvironment
  wheelId: number
  fetchImpl?: (input: string, init?: RequestInit) => Promise<Response>
}): Promise<SplashBrand | null> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), BRAND_TIMEOUT_MS)
  try {
    const response = await fetchImpl(`${partsApiBaseURL(env)}/api/v1/parts/${wheelId}/brand`, {
      signal: controller.signal,
    })
    if (!response.ok) return null
    const body = (await response.json()) as { data?: { brand?: { name?: unknown; logo?: unknown } | null } }
    const brand = body?.data?.brand
    if (!brand || typeof brand.name !== 'string' || brand.name.trim() === '') return null
    return { name: brand.name.trim(), logoURL: httpURL(brand.logo) }
  } catch {
    return null
  } finally {
    clearTimeout(timer)
  }
}
